import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Card, Row, Col, Badge, Image } from 'react-bootstrap';
import { FiSearch, FiCheck, FiDollarSign, FiX } from 'react-icons/fi';
import { motion } from 'framer-motion';

const ProductModal = ({ show, handleClose, products, onSelect, selectedProductId }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [activeFilter, setActiveFilter] = useState('all');
    const categories = ['all', 'Smartphones', 'Laptops'];

    useEffect(() => {
        setFilteredProducts(products);
    }, [products]);

    const handleSearch = (e) => {
        const term = e.target.value.toLowerCase();
        setSearchTerm(term);
        applyFilters(term, activeFilter);
    };

    const handleFilter = (filter) => {
        setActiveFilter(filter);
        applyFilters(searchTerm, filter);
    };

    const applyFilters = (searchTerm, filter) => {
        let filtered = products;
        if (filter !== 'all') {
            filtered = filtered.filter(p => p.category && p.category.toLowerCase() === filter.toLowerCase());
        }
        if (searchTerm.trim() !== '') {
            filtered = filtered.filter(p =>
                p.product_name.toLowerCase().includes(searchTerm) ||
                (p.category && p.category.toLowerCase().includes(searchTerm)) ||
                (p.sku && p.sku.toLowerCase().includes(searchTerm))
            );
        }
        setFilteredProducts(filtered);
    };

    const clearFilters = () => {
        setSearchTerm('');
        setActiveFilter('all');
        setFilteredProducts(products);
    };

    return (
        <Modal show={show} onHide={handleClose} size="xl" centered className="product-modal" style={{ height: '100vh', overflow: 'hidden' }}>
            <Modal.Header className="border-bottom bg-white" style={{ position: 'sticky', top: 0, zIndex: 1050 }}>
                <div className="w-100">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <Modal.Title className="fw-bold mb-0 fs-5">Select Product</Modal.Title>
                        <Button variant="outline-dark" size="sm" onClick={clearFilters} className="d-flex align-items-center gap-1 border px-3">
                            <FiX size={14} /> Clear
                        </Button>
                    </div>

                    <div className="mb-3">
                        <div className="input-group border rounded-3 overflow-hidden">
                            <span className="input-group-text bg-white border-0">
                                <FiSearch className="text-secondary" />
                            </span>
                            <Form.Control
                                type="text"
                                placeholder="Search products"
                                value={searchTerm}
                                onChange={handleSearch}
                                className="border-0 shadow-none"
                                autoFocus
                            />
                        </div>
                    </div>

                    <div className="mb-3">
                        <div className="position-relative">
                            <div className="d-flex gap-4">
                                {categories.map((category, index) => (
                                    <motion.div
                                        key={category}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                    >
                                        <button
                                            onClick={() => handleFilter(category)}
                                            className={`position-relative border-0 bg-transparent px-0 pb-2 ${activeFilter === category ? 'text-dark fw-bold' : 'text-secondary'}`}
                                            style={{
                                                cursor: 'pointer',
                                                outline: 'none',
                                                fontSize: '0.9rem',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            {category === 'all' ? 'All Products' : category}
                                            {activeFilter === category && (
                                                <motion.div
                                                    layoutId="activeFilter"
                                                    className="position-absolute bottom-0 start-0 w-100 bg-dark"
                                                    style={{ height: '2px', borderRadius: '1px' }}
                                                />
                                            )}
                                        </button>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center bg-light rounded-2 px-3 py-2">
                        <div className="d-flex align-items-center gap-2">
                            <span className="text-dark fw-medium" style={{ fontSize: '0.85rem' }}>Showing</span>
                            <Badge bg="dark" className="px-2 py-1">{filteredProducts.length}</Badge>
                            <span className="text-dark fw-medium" style={{ fontSize: '0.85rem' }}>of</span>
                            <Badge bg="secondary" className="px-2 py-1">{products.length}</Badge>
                            <span className="text-dark fw-medium" style={{ fontSize: '0.85rem' }}>products</span>
                        </div>
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body className="pt-3 pb-0" style={{ maxHeight: 'calc(85vh - 270px)', overflowY: 'auto' }}>
                {products.length === 0 ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-dark mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <h5 className="text-dark mb-2">Loading products...</h5>
                        <p className="text-secondary">Please wait while we fetch the products</p>
                    </div>
                ) : filteredProducts.length === 0 ? (
                    <div className="text-center py-5">
                        <div className="display-1 text-secondary mb-3">
                            {searchTerm || activeFilter !== 'all' ? '🔍' : '📦'}
                        </div>
                        <h5 className="text-dark mb-2">
                            {searchTerm || activeFilter !== 'all' ? 'No matching products found' : 'No products available'}
                        </h5>
                        <p className="text-secondary mb-3">
                            {searchTerm || activeFilter !== 'all' ? 'Try different search terms or clear filters' : 'Please check back later'}
                        </p>
                        {(searchTerm || activeFilter !== 'all') && (
                            <Button variant="outline-dark" size="sm" onClick={clearFilters} className="px-3">
                                Clear Filters
                            </Button>
                        )}
                    </div>
                ) : (
                    <Row className="g-3">
                        {filteredProducts.map((product) => (
                            <Col key={product.product_id} xs={12} sm={6} md={4} lg={3}>
                                <motion.div
                                    whileHover={{ y: -4 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <Card
                                        className={`h-100 border cursor-pointer ${selectedProductId === product.product_id
                                            ? 'border-3 border-white'
                                            : 'border-light'
                                            }`}
                                        onClick={() => onSelect(product)}
                                        style={{
                                            cursor: 'pointer',
                                            transition: 'all 0.3s',
                                            borderRadius: '16px',
                                            overflow: 'hidden',
                                            background: selectedProductId === product.product_id
                                                ? 'linear-gradient(135deg, #221c8bff 0%, rgba(255, 89, 172, 0.7) 70%)'
                                                : '#ffffff',
                                            color: selectedProductId === product.product_id ? 'white' : 'inherit',
                                            boxShadow: selectedProductId === product.product_id
                                                ? '0 12px 30px rgba(34, 28, 139, 0.4)'
                                                : '0 4px 12px rgba(0,0,0,0.08)'
                                        }}
                                    >
                                        <div className="position-relative" style={{
                                            height: '160px',
                                            backgroundColor: selectedProductId === product.product_id ? 'rgba(255, 255, 255, 0.1)' : '#f8f9fa',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backdropFilter: selectedProductId === product.product_id ? 'blur(2px)' : 'none'
                                        }}>
                                            <Card.Img
                                                variant="top"
                                                src={product.product_img || 'https://via.placeholder.com/300x200?text=No+Image'}
                                                alt={product.product_name}
                                                style={{
                                                    height: '140px',
                                                    width: 'auto',
                                                    maxWidth: '100%',
                                                    objectFit: 'contain',
                                                    padding: '10px',
                                                    filter: selectedProductId === product.product_id ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' : 'none'
                                                }}
                                                onError={(e) => {
                                                    e.target.src = 'https://via.placeholder.com/300x200?text=No+Image';
                                                }}
                                            />
                                            {selectedProductId === product.product_id && (
                                                <motion.div
                                                    initial={{ scale: 0, rotate: -180 }}
                                                    animate={{ scale: 1, rotate: 0 }}
                                                    className="position-absolute top-0 end-0 m-2"
                                                >
                                                    <Badge bg="white" text="dark" className="rounded-circle p-2 shadow-lg">
                                                        <FiCheck size={16} className="text-primary" />
                                                    </Badge>
                                                </motion.div>
                                            )}
                                            <Badge
                                                bg={product.status === 'Active'
                                                    ? (selectedProductId === product.product_id ? 'white' : 'success')
                                                    : 'warning'
                                                }
                                                text={selectedProductId === product.product_id && product.status === 'Active' ? 'dark' : 'white'}
                                                className="position-absolute bottom-0 start-0 m-2 shadow-sm"
                                            >
                                                {product.status || 'Inactive'}
                                            </Badge>
                                        </div>

                                        <Card.Body className="p-3">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <Badge
                                                    bg={selectedProductId === product.product_id ? 'rgba(255, 255, 255, 0.2)' : 'secondary'}
                                                    text={selectedProductId === product.product_id ? 'white' : 'white'}
                                                    className="fw-normal px-2 py-1 border-0"
                                                    style={{
                                                        background: selectedProductId === product.product_id
                                                            ? 'rgba(255, 255, 255, 0.2)'
                                                            : undefined,
                                                        backdropFilter: selectedProductId === product.product_id ? 'blur(4px)' : 'none'
                                                    }}
                                                >
                                                    {product.category || 'Uncategorized'}
                                                </Badge>
                                                <small className={selectedProductId === product.product_id ? 'text-white-70' : 'text-muted'} style={{ fontSize: '0.7rem' }}>
                                                    SKU: {product.sku || 'N/A'}
                                                </small>
                                            </div>

                                            <Card.Title
                                                className={`mb-2 ${selectedProductId === product.product_id ? 'text-white' : 'text-dark'}`}
                                                style={{
                                                    fontSize: '0.9rem',
                                                    fontWeight: '600',
                                                    lineHeight: '1.4',
                                                    height: '2.8em',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                {product.product_name || 'Unnamed Product'}
                                            </Card.Title>

                                            <div className="mb-3" style={{ fontSize: '0.75rem' }}>
                                                <div className="d-flex justify-content-between mb-1">
                                                    <span className={selectedProductId === product.product_id ? 'text-white-70' : 'text-muted'}>Color:</span>
                                                    <span className={selectedProductId === product.product_id ? 'text-white fw-medium' : 'text-dark'}>{product.color || 'N/A'}</span>
                                                </div>
                                                <div className="d-flex justify-content-between mb-1">
                                                    <span className={selectedProductId === product.product_id ? 'text-white-70' : 'text-muted'}>Storage:</span>
                                                    <span className={selectedProductId === product.product_id ? 'text-white fw-medium' : 'text-dark'}>{product.capacity || 'N/A'}</span>
                                                </div>
                                                <div className="d-flex justify-content-between">
                                                    <span className={selectedProductId === product.product_id ? 'text-white-70' : 'text-muted'}>Stock:</span>
                                                    <span className={`fw-bold ${product.quantity > 0
                                                        ? (selectedProductId === product.product_id ? 'text-success-light' : 'text-success')
                                                        : (selectedProductId === product.product_id ? 'text-warning-light' : 'text-danger')}`}
                                                        style={selectedProductId === product.product_id && product.quantity > 0
                                                            ? { color: '#b0f7b0' }
                                                            : selectedProductId === product.product_id
                                                                ? { color: '#ffd166' }
                                                                : {}
                                                        }
                                                    >
                                                        {product.quantity || 0} units
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="border-top pt-2" style={{ borderColor: selectedProductId === product.product_id ? 'rgba(255,255,255,0.3)' : '#dee2e6' }}>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <div className="d-flex align-items-center gap-1">
                                                            <FiDollarSign className={selectedProductId === product.product_id ? 'text-white' : 'text-success'} size={14} />
                                                            <span className={`fw-bold fs-5 ${selectedProductId === product.product_id ? 'text-white' : 'text-dark'}`}>
                                                                ${parseFloat(product.sales_price || 0).toFixed(2)}
                                                            </span>
                                                        </div>
                                                        {product.price && parseFloat(product.price) !== parseFloat(product.sales_price) && (
                                                            <small className={selectedProductId === product.product_id ? 'text-white-70' : 'text-muted'} style={{ fontSize: '0.75rem' }}>
                                                                Was: ${parseFloat(product.price || 0).toFixed(2)}
                                                            </small>
                                                        )}
                                                    </div>
                                                    {selectedProductId === product.product_id && (
                                                        <motion.div
                                                            initial={{ scale: 0 }}
                                                            animate={{ scale: 1 }}
                                                        >
                                                            <FiCheck className="text-white" size={18} />
                                                        </motion.div>
                                                    )}
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </motion.div>
                            </Col>
                        ))}
                    </Row>
                )}
            </Modal.Body>

            <Modal.Footer className="border-top bg-white py-3" style={{ position: 'sticky', bottom: 0, zIndex: 1050 }}>
                <div className="w-100">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            {selectedProductId && (
                                <Badge
                                    bg="transparent"
                                    className="px-3 py-2 d-flex align-items-center gap-2 border-0"
                                    style={{
                                        background: 'linear-gradient(135deg, #221c8bff 0%, rgba(255, 89, 172, 1) 100%)',
                                        color: 'white'
                                    }}
                                >
                                    <FiCheck /> Product Selected
                                </Badge>
                            )}
                        </div>
                        <div className="d-flex gap-2">
                            <Button variant="outline-dark" onClick={handleClose} size="sm" className="px-4">
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleClose}
                                size="sm"
                                className="px-4 border-0"
                                style={{
                                    background: 'linear-gradient(135deg, #221c8bff 0%, rgba(255, 89, 172, 1) 100%)',
                                    border: 'none'
                                }}
                            >
                                Confirm
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default ProductModal;